import {
  CheckCircleOutline,
  CloseOutlined,
  Done,
  PostAddOutlined,
} from '@mui/icons-material';
import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { VenueDateBasic } from '@/domain/project-venue-dates/entities/project-venue-dates.entity';
import { CloseButton } from '@/ui/components/button/CloseButton';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSCard } from '@/ui/components/FSCard';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { formatImportedDates } from './format-imported-dates';

interface IProps {
  open: boolean;
  onClose: () => void;
  onImportDates: (dates: VenueDateBasic[]) => void;
  project: ProjectVenue;
}

export function ModalImportCsv({
  open,
  onClose,
  onImportDates,
  project,
}: IProps) {
  const [importFinished, setImportFinished] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [creationResult, setCreationResult] = useState<{
    dates: VenueDateBasic[];
    datesCreated: number;
    datesNotCreated: number;
  }>({ dates: [], datesCreated: 0, datesNotCreated: 0 });

  const handleClose = () => {
    setImportFinished(false);
    setIsLoading(false);
    onClose();
    if (creationResult.datesCreated > 0) {
      onImportDates(creationResult.dates);
    }
  };

  return (
    <FSModal open={open} onClose={handleClose} maxWidth={550}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <FSModalTitle title={translate('ProjectsVenue.import-csv-table')} />
          <CloseButton onClick={handleClose} />
        </Stack>

        {!importFinished && !isLoading && (
          <ImportingDisplay
            setIsLoading={setIsLoading}
            setImportFinished={setImportFinished}
            project={project}
            setCreationResult={setCreationResult}
          />
        )}

        {importFinished && !isLoading && (
          <ImportFinishedDisplay
            onClose={handleClose}
            creationResult={creationResult}
          />
        )}

        {isLoading && (
          <Stack
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <PostAddOutlined sx={{ fontSize: 48 }} />
            <Typography
              variant="h3"
              sx={{ fontSize: 36, fontWeight: 800, mt: 2 }}
            >
              {translate('VenueDates.ImportFilesModal.importing')}
            </Typography>
            <CircularProgress size={24} sx={{ my: 4 }} />
          </Stack>
        )}
      </Stack>
    </FSModal>
  );
}

const ImportingDisplay = ({
  setIsLoading,
  setImportFinished,
  project,
  setCreationResult,
}: {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setImportFinished: React.Dispatch<React.SetStateAction<boolean>>;
  project: ProjectVenue;
  setCreationResult: React.Dispatch<
    React.SetStateAction<{
      dates: VenueDateBasic[];
      datesCreated: number;
      datesNotCreated: number;
    }>
  >;
}) => {
  // const downloadTemplate = (e: React.MouseEvent<HTMLButtonElement>) => {
  //   e.stopPropagation();
  // };

  const handleDrop = async (acceptedFiles: File[]) => {
    setIsLoading(true);
    acceptedFiles.forEach(async (file) => {
      await formatImportedDates({
        project,
        setCreationResult,
        file,
      });
      setIsLoading(false);
      setImportFinished(true);
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
  });

  return (
    <Stack
      {...getRootProps()}
      className="dropzone"
      sx={{
        cursor: 'pointer',
        p: 2,
        border: '1px dashed',
        borderColor: isDragActive
          ? ColorsRaw.anthracite.Anthracite_5
          : ColorsRaw.anthracite.Anthracite_4,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <input {...getInputProps()} />
      <PostAddOutlined sx={{ fontSize: 48 }} />
      <Typography variant="h3" sx={{ fontSize: 36, fontWeight: 800, mt: 2 }}>
        {translate('VenueDates.ImportFilesModal.drag-and-drop')}
      </Typography>
      <Typography sx={{ color: ColorsRaw.secondary.Light_Grey }}>
        .csv
      </Typography>

      <FSCard
        variant="elevation"
        sx={{
          py: 2,
          px: 5,
          bgcolor: ColorsRaw.anthracite.Warm_Anthracite_2,
          cursor: 'pointer',
          textAlign: 'center',
          mt: 3,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 'bold', mb: 1 }}>
          {translate('VenueDates.ImportFilesModal.must-respect')}
        </Typography>
        <Typography>
          <Done
            sx={{
              fontSize: 16,
              mr: 1,
            }}
          />
          {translate('VenueDates.ImportFilesModal.column-date')}
        </Typography>
        <Typography sx={{ mt: 0.5 }}>
          <Done
            sx={{
              fontSize: 16,
              mr: 1,
            }}
          />
          {translate('VenueDates.ImportFilesModal.column-name')}
        </Typography>
        {/* <FSSecondaryButton sx={{ mt: 2 }} onClick={downloadTemplate}>
          {translate('VenueDates.ImportFilesModal.download-template')}
        </FSSecondaryButton> */}
      </FSCard>

      <FSPrimaryButton sx={{ mt: 2 }}>
        {translate('VenueDates.ImportFilesModal.import-file')}
      </FSPrimaryButton>
    </Stack>
  );
};

const ImportFinishedDisplay = ({
  onClose,
  creationResult,
}: {
  onClose: () => void;
  creationResult: {
    dates: VenueDateBasic[];
    datesCreated: number;
    datesNotCreated: number;
  };
}) => {
  return (
    <Stack
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <PostAddOutlined sx={{ fontSize: 48 }} />
      <Typography variant="h3" sx={{ fontSize: 36, fontWeight: 800, mt: 2 }}>
        {translate('VenueDates.ImportFilesModal.import-result')}
      </Typography>
      <Stack
        direction={'row'}
        mt={2}
        justifyContent={'space-between'}
        width={300}
      >
        <Stack sx={{ width: '100%', textAlign: 'right' }}>
          <Typography>
            {translate('VenueDates.ImportFilesModal.dates-created')}
          </Typography>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'end'}>
            <CheckCircleOutline
              sx={{ color: ColorsRaw.scores.Green, mr: 0.5, mt: 0.5 }}
            />
            <Typography fontWeight={'bold'} fontSize={24}>
              {creationResult.datesCreated}
            </Typography>
          </Stack>
        </Stack>
        <Divider orientation="vertical" sx={{ mx: 4, height: '60px' }} />
        <Stack sx={{ width: '100%' }}>
          <Typography>
            {translate('VenueDates.ImportFilesModal.dates-not-created')}
          </Typography>
          <Stack direction={'row'} alignItems={'center'}>
            <Typography
              fontWeight={'bold'}
              fontSize={24}
              sx={{ color: ColorsRaw.error.Error_on }}
            >
              {creationResult.datesNotCreated}
            </Typography>
            <CloseOutlined
              sx={{ color: ColorsRaw.error.Error, ml: 0.5, mt: 0.5 }}
            />
          </Stack>
        </Stack>
      </Stack>
      <FSPrimaryButton sx={{ mt: 3 }} onClick={onClose}>
        {creationResult.datesCreated > 0
          ? translate('Form.next')
          : translate('Form.close')}
      </FSPrimaryButton>
    </Stack>
  );
};
